import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { orderId, sspSubmitOrder, toggleSspModels } from 'pages/provisioning/aws/internal-url-onboarding/store';
import logger from 'utils/logger';
import { resetOrder, resetToggleSspModal, formOrderObjFromOrderDetails } from './utils/index';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aws/internal-url-onboarding/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/aws/internal-url-onboarding/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/aws/internal-url-onboarding/SspForm'));
const OrderSuccessDialog = React.lazy(() => import('pages/provisioning/aws/internal-url-onboarding/OrderSuccessDialog'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aws/internal-url-onboarding/ShowCostDetails'));

const AWSInternalUrlOnboardProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    // Helper function to fetch landing zone details
    const fetchLandingZoneDetails = async (orderData) => {
        const isProduction = orderData?.environment === 'Production';
        if (
            orderData?.appProfile &&
            orderData?.sector &&
            orderData?.department &&
            orderData?.appRegion &&
            orderData?.environment &&
            (!isProduction || ['yes', 'no'].includes(orderData?.isHighAvailability))
        ) {
            const payload = {
                applicationServiceName: orderData.appProfile,
                sector: orderData.sector,
                environment: orderData.environment,
                department: orderData.department,
                applicationRegion: orderData.appRegion,
                ...(isProduction && { highAvailability: orderData?.isHighAvailability === 'yes' }),
            };
            try {
                const landingZoneResponse = await axios.post(endPoints.aws.Ec2.awsLandingZone, payload);
                return landingZoneResponse?.data;
            } catch (error) {
                logger.error('Landing zone API error:', error);
                return null;
            }
        }
        return null;
    };

    // Helper function to fetch target landing zone details
    const fetchTargetLandingZoneDetails = async (orderData) => {
        if (orderData?.targetAppProfile && orderData?.targetSector && orderData?.targetDepartment && orderData?.appRegion && orderData?.environment) {
            const payload = {
                applicationServiceName: orderData.targetAppProfile,
                sector: orderData.targetSector,
                environment: orderData.environment,
                department: orderData.targetDepartment,
                applicationRegion: orderData.appRegion,
            };
            try {
                const landingZoneResponse = await axios.post(endPoints.aws.Ec2.awsLandingZone, payload);
                return landingZoneResponse?.data;
            } catch (error) {
                logger.error('Landing zone API error:', error);
                return null;
            }
        }
        return null;
    };

    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            const landingZoneDetails = await fetchLandingZoneDetails(orderObj);
            const targetLandingZoneDetails = await fetchTargetLandingZoneDetails(orderObj);

            // Derive awsAccount from awsAccountId during edit/resubmit
            const matchedAccount = landingZoneDetails?.awsAccounts?.find((account) => account?.accountId === orderObj?.awsAccountId);
            const matchedTargetAccount = targetLandingZoneDetails?.awsAccounts?.find((account) => account?.accountId === orderObj?.targetCrossAccountId);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                landingZoneDetails,
                targetLandingZoneDetails,
                emptyCost: costs,
                ...(matchedAccount && { awsAccount: matchedAccount.accountName }),
                ...(matchedTargetAccount && { targetCrossAccount: matchedTargetAccount.accountName, targetCrossAccountId: matchedTargetAccount.accountId }),
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetToggleSspModels(resetToggleSspModal);
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    React.useEffect(() => {
        return () => reset();
    }, []);

    return (
        <>
            <Helmet>
                <title>AWS Internal URL Onboarding</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <OrderSuccessDialog />
                        <ApplicationDetails />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(AWSInternalUrlOnboardProvisioningPage);
